import type { TFacet } from '@lucimark/shared/models/playlist'
import type { IAssetIndex, IQueueFrame } from '@/types'
import { facetAssetIndexKey } from '@lucimark/shared/helpers/facetAssetIndexKey'

/**
 * How long the presentation surface may stay empty while a non-empty queue exists before
 * VBuffer kicks recovery (asset rebuild + worker resync + bootstrap).
 * Mid-tier Android boxes often need 1–2s for IndexedDB → blob URL indexing after schedule ready.
 */
export const EMPTY_PLAYBACK_SURFACE_STALL_MS = 2_000

/**
 * After this much empty-surface time, recovery may run even while a pending commit is waiting
 * on the asset index. Pending retries alone can leave the brand cube up for ~10s while the
 * playlist clock already advances (timers overlay looks "started").
 */
export const EMPTY_PLAYBACK_SURFACE_PENDING_STALL_MS = 3_500

/** Cooldown between empty-surface recovery attempts (avoid thrash while assets still downloading). */
export const EMPTY_PLAYBACK_SURFACE_RECOVERY_COOLDOWN_MS = 3_000

/**
 * Hard ceiling for `applyRegistrationPlaybackResync`. Unbounded `buildAssetIndex` /
 * `syncPlaybackWorkerStateNow` awaits left `queueAssetResyncInProgress` latched forever (#1362).
 */
export const REGISTRATION_PLAYBACK_RESYNC_MAX_MS = 30_000

/**
 * After this long with `assetResyncInProgress` still true, empty-surface recovery may arm anyway.
 * Defense in depth if a non-registration path latches the flag without clearing it (#1362).
 */
export const ASSET_RESYNC_RECOVERY_GUARD_MAX_MS = 30_000

/**
 * Consecutive empty-surface recoveries that leave the surface empty before we stop silent looping
 * and leave the durable `empty_playback_surface` fault as the operator signal (#1362).
 */
export const EMPTY_PLAYBACK_SURFACE_MAX_RECOVERY_ATTEMPTS = 5

/**
 * How long empty-surface recovery polls for a post-bootstrap paint / pending commit before
 * scoring the attempt. Bootstrap notify crosses Comlink and may land after the previous
 * portalBookPresentationCommitChain has already settled — racing that chain alone looked
 * empty in the same tick and burned the 5-attempt budget (#1553).
 */
export const EMPTY_PLAYBACK_SURFACE_RECOVERY_COMMIT_WAIT_MS = 1_500

/** Poll interval while settling empty-surface recovery after bootstrap (#1553). */
export const EMPTY_PLAYBACK_SURFACE_RECOVERY_COMMIT_POLL_MS = 50

/** Retry asset-index build when a pending surface commit is still missing entries after a build. */
export const PENDING_SURFACE_COMMIT_RETRY_MS = 1_200

/**
 * Max deferred rebuilds before resetting the attempt counter and continuing to wait.
 * Never force-commit without an asset-index entry — that flashes SECTION UNAVAILABLE.
 */
export const PENDING_SURFACE_COMMIT_MAX_RETRIES = 8

type FacetWithAsset = TFacet & { asset: string }

function facetHasAsset(facet: TFacet | undefined): facet is FacetWithAsset {
  return typeof facet?.asset === 'string' && facet.asset.trim() !== ''
}

/**
 * Facets that must be indexed before a presentation commit.
 * Prefer `main` only — secondary sections must not block the whole surface (cube / black stall).
 * When `main` has no asset URN, fall back to every facet with an asset (legacy / overlay-only rows).
 */
export function facetsRequiredForPlaybackSurfaceCommit(
  frame: Pick<IQueueFrame, 'facets'>
): FacetWithAsset[] {
  const facets = frame.facets ?? {}
  const main = facets.main
  if (facetHasAsset(main)) {
    return [main]
  }
  const required: FacetWithAsset[] = []
  for (const facet of Object.values(facets)) {
    if (facetHasAsset(facet)) {
      required.push(facet)
    }
  }
  return required
}

/**
 * True when any buffer frame still lacks an {@link IAssetIndex} entry for facets required to paint.
 */
export function playbackBufferFramesMissingAssetIndex(
  frames: readonly Pick<IQueueFrame, 'facets'>[],
  assetIndex: IAssetIndex
): boolean {
  for (const frame of frames) {
    for (const facet of facetsRequiredForPlaybackSurfaceCommit(frame)) {
      if (assetIndex[facetAssetIndexKey(facet)] === undefined) {
        return true
      }
    }
  }
  return false
}

/**
 * Facet asset URNs required by pending frames but missing from the in-memory campaign asset list.
 */
export function pendingSurfaceCommitUrnsMissingFromCampaign(input: {
  pendingFrames: readonly Pick<IQueueFrame, 'facets'>[]
  playbackAssetUrns: ReadonlySet<string>
}): string[] {
  const missing: string[] = []
  for (const frame of input.pendingFrames) {
    for (const facet of facetsRequiredForPlaybackSurfaceCommit(frame)) {
      const key = facetAssetIndexKey(facet)
      if (!input.playbackAssetUrns.has(facet.asset) && !input.playbackAssetUrns.has(key)) {
        missing.push(facet.asset)
      }
    }
  }
  return missing
}

/**
 * True when any facet required for a pending surface commit references an asset URN that is
 * no longer in the current campaign asset list **and** no longer on the active schedule.
 * Retries cannot recover — true #1352 left-campaign divergence.
 *
 * When the URN is still on the schedule but missing from `getPlaybackAssets()`, that is incomplete
 * materialization (#1574) — caller must rematerialize, not abandon.
 */
export function pendingSurfaceCommitUrnsUnsatisfiable(input: {
  pendingFrames: readonly Pick<IQueueFrame, 'facets'>[]
  playbackAssetUrns: ReadonlySet<string>
  /** When provided, URNs still present here are incomplete materialization, not #1352. */
  scheduleAssetUrns?: ReadonlySet<string>
}): boolean {
  const missing = pendingSurfaceCommitUrnsMissingFromCampaign(input)
  if (missing.length === 0) {
    return false
  }
  if (input.scheduleAssetUrns == null) {
    return true
  }
  for (const urn of missing) {
    if (!input.scheduleAssetUrns.has(urn)) {
      return true
    }
  }
  return false
}

/** Missing-from-campaign URNs that are still referenced by the active schedule (#1574). */
export function pendingSurfaceCommitUrnsIncompleteMaterialization(input: {
  pendingFrames: readonly Pick<IQueueFrame, 'facets'>[]
  playbackAssetUrns: ReadonlySet<string>
  scheduleAssetUrns: ReadonlySet<string>
}): string[] {
  return pendingSurfaceCommitUrnsMissingFromCampaign(input)
    .filter(urn => input.scheduleAssetUrns.has(urn))
}

/**
 * Cold join / DL rotating-embed: an incomplete campaign list while a force reload or
 * registration asset resync is already running must wait — stacking `forceAssetReload`
 * restarts `fetchAssets` and thrashes pending surface commits.
 */
export function shouldForceReloadIncompleteCampaignAssets(input: {
  incompleteUrns: readonly string[]
  assetReloadInFlight: boolean
  assetResyncInProgress: boolean
}): boolean {
  if (input.incompleteUrns.length === 0) {
    return false
  }
  if (input.assetReloadInFlight || input.assetResyncInProgress) {
    return false
  }
  return true
}

/** Whether another deferred pending-commit asset rebuild should be armed. */
export function shouldSchedulePendingCommitRetry(input: {
  attemptCount: number
  maxAttempts: number
}): boolean {
  return input.attemptCount < input.maxAttempts
}

/**
 * Asset-index entries that are safe to revoke after a campaign asset list rebuild.
 * Keep URNs still referenced by the on-air / sticky surface so same-identity on-air
 * edits cannot flash black while the new blob URL is built (#1352 continuity).
 */
export function assetIndexUrnsSafeToRevoke(input: {
  indexedUrns: readonly string[]
  requiredAssetIds: ReadonlySet<string>
  retainUrns: ReadonlySet<string>
}): string[] {
  return input.indexedUrns.filter(
    assetId => !input.requiredAssetIds.has(assetId) && !input.retainUrns.has(assetId)
  )
}

/**
 * Whether a latched asset-resync flag should still block empty-surface recovery.
 * Past {@link ASSET_RESYNC_RECOVERY_GUARD_MAX_MS} the latch is ignored so a hung rebuild cannot
 * disarm recovery forever (#1362).
 */
export function isAssetResyncBlockingRecovery(input: {
  assetResyncInProgress: boolean
  /** Ms since the flag went true; null when unknown / not in progress. */
  assetResyncElapsedMs: number | null
  maxMs?: number
}): boolean {
  if (!input.assetResyncInProgress) {
    return false
  }
  const maxMs = input.maxMs ?? ASSET_RESYNC_RECOVERY_GUARD_MAX_MS
  if (input.assetResyncElapsedMs == null) {
    return true
  }
  return input.assetResyncElapsedMs < maxMs
}

/** True when consecutive empty-surface recoveries have exhausted the silent-retry budget (#1362). */
export function shouldEscalateEmptyPlaybackSurfaceRecovery(input: {
  attemptCount: number
  maxAttempts?: number
}): boolean {
  const maxAttempts = input.maxAttempts ?? EMPTY_PLAYBACK_SURFACE_MAX_RECOVERY_ATTEMPTS
  return input.attemptCount >= maxAttempts
}

/**
 * Whether a transition hold should drop presentation / buffer-only worker notifies.
 * Content pins share `registrationTransitionHoldActive` without a registration bootstrap waiter;
 * dropping while the renderable surface is already empty left the brand cube forever after OTA (#1553).
 */
export function shouldDropPresentationForTransitionHold(input: {
  registrationHoldActive: boolean
  /** Registration resync is waiting for bootstrap paint (`resolveRegistrationBootstrapCommit`). */
  awaitingRegistrationBootstrapCommit: boolean
  /** Frames currently shown, including a held lastNonEmpty residual. */
  renderableFrameCount: number
}): boolean {
  if (!input.registrationHoldActive) {
    return false
  }
  if (input.awaitingRegistrationBootstrapCommit) {
    return false
  }
  return input.renderableFrameCount > 0
}

/**
 * Score empty-surface recovery after bootstrap + settling paint / pending commit (#1553).
 * Pending asset-index commits stay `deferred` so they do not burn the silent-retry budget.
 */
export function resolveEmptySurfaceRecoveryBootstrapOutcome(input: {
  surfaceFrameCount: number
  pendingCommit: boolean
}): 'painted' | 'deferred' | 'failed' {
  if (input.surfaceFrameCount > 0) {
    return 'painted'
  }
  if (input.pendingCommit) {
    return 'deferred'
  }
  return 'failed'
}

/**
 * Poll until bootstrap paint lands, a pending surface commit appears, or waitMs elapses.
 * Prefer this over racing portalBookPresentationCommitChain — that chain is often already
 * settled when recovery starts, while the Comlink presentation notify is still in flight.
 */
export async function settleEmptySurfaceRecoveryBootstrap(input: {
  waitMs: number
  pollMs?: number
  nowMs?: () => number
  sleep?: (ms: number) => Promise<void>
  sample: () => { surfaceFrameCount: number; pendingCommit: boolean }
}): Promise<'painted' | 'deferred' | 'failed'> {
  const waitMs = Math.max(0, input.waitMs)
  const pollMs = Math.max(1, input.pollMs ?? EMPTY_PLAYBACK_SURFACE_RECOVERY_COMMIT_POLL_MS)
  const nowMs = input.nowMs ?? (() => Date.now())
  const sleep = input.sleep ?? ((ms: number) => new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  }))
  const deadline = nowMs() + waitMs
  let outcome = resolveEmptySurfaceRecoveryBootstrapOutcome(input.sample())
  if (outcome !== 'failed') {
    return outcome
  }
  while (nowMs() < deadline) {
    const remaining = deadline - nowMs()
    if (remaining <= 0) {
      break
    }
    await sleep(Math.min(pollMs, remaining))
    outcome = resolveEmptySurfaceRecoveryBootstrapOutcome(input.sample())
    if (outcome !== 'failed') {
      return outcome
    }
  }
  return resolveEmptySurfaceRecoveryBootstrapOutcome(input.sample())
}

/**
 * Cancellable deadline for `Promise.race` against registration resync (#1362).
 * Call {@link RegistrationPlaybackResyncDeadline.cancel} when the body wins so the timer does not
 * reject later (orphaned rejection). Attach a no-op catch so a late reject after cancel is silent.
 */
export type RegistrationPlaybackResyncDeadline = {
  promise: Promise<never>
  cancel: () => void
}

export function registrationPlaybackResyncDeadline(
  ms: number = REGISTRATION_PLAYBACK_RESYNC_MAX_MS
): RegistrationPlaybackResyncDeadline {
  let timer: ReturnType<typeof setTimeout> | null = null
  const promise = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      timer = null
      reject(new Error(`registration_playback_resync_timeout after ${ms}ms`))
    }, ms)
  })
  /** Swallow late rejects after {@link RegistrationPlaybackResyncDeadline.cancel} / body win. */
  void promise.catch(() => {})
  return {
    promise,
    cancel: () => {
      if (timer != null) {
        clearTimeout(timer)
        timer = null
      }
    }
  }
}

/**
 * Whether VBuffer should run empty-surface recovery (queue ready, nothing on screen, stall elapsed).
 * Skips intentional empty windows: asset rebuild, init bootstrap, post-init suppress.
 * Pending commits only block until {@link EMPTY_PLAYBACK_SURFACE_PENDING_STALL_MS} — longer stalls
 * must recover (the common Android path is pending + advancing playlist timers + brand cube).
 * A latched `assetResyncInProgress` only blocks until {@link ASSET_RESYNC_RECOVERY_GUARD_MAX_MS}.
 */
export function shouldRecoverEmptyPlaybackSurface(input: {
  queueLen: number
  surfaceFrameCount: number
  stallMs: number
  thresholdMs: number
  recoveryInFlight: boolean
  msSinceLastRecovery: number
  cooldownMs: number
  pendingCommit: boolean
  pendingStallThresholdMs: number
  assetResyncInProgress: boolean
  /** When set, a latched resync past the guard ceiling no longer blocks recovery (#1362). */
  assetResyncElapsedMs?: number | null
  assetResyncMaxMs?: number
  /** After N failed recoveries, stop silent looping (#1362). */
  recoveryExhausted?: boolean
  initBootstrapPending: boolean
  workerSyncSuppressed: boolean
}): boolean {
  if (input.recoveryInFlight) {
    return false
  }
  if (input.recoveryExhausted) {
    return false
  }
  if (isAssetResyncBlockingRecovery({
    assetResyncInProgress: input.assetResyncInProgress,
    assetResyncElapsedMs: input.assetResyncElapsedMs ?? null,
    maxMs: input.assetResyncMaxMs
  })) {
    return false
  }
  if (input.initBootstrapPending || input.workerSyncSuppressed) {
    return false
  }
  if (input.queueLen <= 0 || input.surfaceFrameCount > 0) {
    return false
  }
  if (input.msSinceLastRecovery < input.cooldownMs) {
    return false
  }
  if (input.pendingCommit && input.stallMs < input.pendingStallThresholdMs) {
    return false
  }
  return input.stallMs >= input.thresholdMs
}

/**
 * Hold playlist/frame timer overlay updates while the brand cube is on screen
 * (queue ready, presentation surface still empty). Avoids "Campaign 0:05" over the saturn mark.
 */
export function shouldPublishPlaybackTimingOverlay(input: {
  queueLen: number
  surfaceFrameCount: number
}): boolean {
  if (input.queueLen <= 0) {
    return true
  }
  return input.surfaceFrameCount > 0
}

/**
 * Shortest cube window that earns a frame-clock re-phase on first paint. Normal frame advances
 * commit with an already-painted surface, so they never reach this gate; the guard only keeps
 * sub-frame surface gaps (worker resync, hold release) from re-phasing a loop that is on time.
 */
export const FIRST_PAINT_CLOCK_REPHASE_MIN_EMPTY_MS = 600

/**
 * Whether the frame clock must restart at the moment pixels finally reach the screen.
 *
 * The playlist clock starts when the scheduler bootstraps, not when the first creative paints.
 * After a reboot the brand cube can hold the surface for seconds while IndexedDB assets are
 * indexed, and the clock spends the slot meanwhile — a 13s video then mounts at 0 with only ~2s
 * of dwell left, which reads as "the video started at 10s". Re-phasing on the first paint gives
 * the visible frame a full slot from the moment it is actually on air.
 */
export function shouldRephaseFrameClockOnFirstPaint(input: {
  /** Rendered frame count before this commit (0 while the cube is up). */
  previousSurfaceFrameCount: number
  nextSurfaceFrameCount: number
  /** How long the surface has been empty, or null when it was never tracked as empty. */
  emptyForMs: number | null
  minEmptyMs: number
  /** Continuous-media splits carry their own `currentTime` offset — never re-phase those. */
  seamlessContinuous: boolean
}): boolean {
  if (input.seamlessContinuous) {
    return false
  }
  if (input.nextSurfaceFrameCount <= 0 || input.previousSurfaceFrameCount > 0) {
    return false
  }
  if (input.emptyForMs == null) {
    return false
  }
  return input.emptyForMs >= input.minEmptyMs
}
